import { useCallback } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { snakeCase } from "lodash";

import { listSoilSightScenarios, resolveFieldGeometry } from "services";

import {
  getUserFacingApiErrorMessage,
  productAnalytics,
  useCreateSoilSightScenario,
  useGetPracticeCrops,
  useMutateSoilSightOutcomes,
  useNavigate,
  useReplaceSoilSightCropYears,
  useTranslation,
  useUserContext,
} from "utils";

import { LIST_SOIL_SIGHT_SCENARIOS, TIMEOUT, TIMEOUT_LONG_RUNNING } from "consts";

import { Message } from "components";

import { useFieldImportPersist } from "pages/app/scenario-analysis/hooks/useFieldImportPersist";
import { toApiCropYear } from "pages/app/scenario-analysis/utils/scenarioApiMappers";
import { classifyForecastError } from "pages/app/scenario-analysis/utils/soilSightTracking";
import { classifyLift, getScenarioUpliftPercent } from "pages/app/scenario-analysis/utils/uplift";
import { useScenarioOnboarding } from "pages/app/scenario-onboarding/ScenarioOnboardingContext";
import { useGetSoilSightOnboardingSetting, useUpdateSoilSightOnboardingSetting } from "pages/app/scenario-onboarding/hooks/useSoilSightOnboardingSetting";
import { validateBaselineCropYears } from "pages/app/scenario-onboarding/utils/baselineValidation";
import { buildCropCalendarInfoById, generateChangeScenario } from "pages/app/scenario-onboarding/utils/changeScenarioGenerator";
import { mergeInProgressDraft } from "pages/app/scenario-onboarding/utils/onboardingState";

// The save's own request timeouts already bound it (settings + create + list, then the parallel
// crop-years/refinements pair). Only a backstop against an un-timed call entering that path —
// expiring early would let both paths create a project.
const DRAFT_SAVE_DRAIN_TIMEOUT_MS = TIMEOUT * 3 + TIMEOUT_LONG_RUNNING;

/** Waits for the queued draft save, giving up after DRAFT_SAVE_DRAIN_TIMEOUT_MS. */
const drainDraftSaveQueue = (queue: Promise<void>): Promise<void> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const bound = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, DRAFT_SAVE_DRAIN_TIMEOUT_MS);
  });
  return Promise.race([queue, bound]).finally(() => clearTimeout(timer));
};

/**
 * Step-1 CTA orchestration:
 * validate → write the in-progress key (BEFORE project creation, so a draft can never be
 * mistaken for a grandfathered legacy user) → persist the baseline rotation → auto-generate
 * the change scenario → single project-wide /run → hand everything to the flow context and
 * open step 2.
 */
export const useSubmitBaselineAndGenerate = () => {
  const { t } = useTranslation("scenarioAnalysis");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, reloadUserData } = useUserContext();
  const { data: practiceCrops } = useGetPracticeCrops({ enabled: true });
  const { onboardingState } = useGetSoilSightOnboardingSetting();

  const {
    cropYears,
    selectedFarmId,
    selectedFieldId,
    importMeta,
    scenarioRefs,
    calcStatus,
    setValidationAttempted,
    setCalcStatus,
    setScenarioRefs,
    setCropYears,
    setChangeCropYears,
    setOutcomes,
    draftSaveQueueRef,
    persistedProjectIdRef,
  } = useScenarioOnboarding();

  const { persistFieldImport } = useFieldImportPersist();
  const createScenario = useCreateSoilSightScenario();
  const replaceCropYears = useReplaceSoilSightCropYears();
  const mutateOutcomes = useMutateSoilSightOutcomes();
  const updateOnboardingSetting = useUpdateSoilSightOnboardingSetting();

  const submitBaseline = useCallback(async (): Promise<boolean> => {
    setValidationAttempted(true);

    const validation = validateBaselineCropYears(cropYears);
    if (!validation.valid || !selectedFarmId || !selectedFieldId) {
      return false;
    }

    const activeFarmProjectId = user?.farm?.id === selectedFarmId ? user?.farm?.soilSightProjectId ?? null : null;

    setCalcStatus("calculating");
    // Move to step 2 right away — it renders the in-wizard "Calculating your forecast"
    // state until the outcomes land (and bounces back to step 1 on error).
    navigate("/scenarios-onboarding/step-2");
    try {
      // In-progress key first — this ordering is what keeps a mid-draft user distinguishable
      // from a legacy (grandfathered) user, so it must precede project creation.
      await updateOnboardingSetting.mutateAsync(
        mergeInProgressDraft(onboardingState, {
          farmId: selectedFarmId,
          importedFieldId: importMeta?.importedFieldId,
          importBannerDismissed: importMeta?.bannerDismissed,
          landingSeen: true,
        })
      );

      // An autosave already in flight would create its own project (both resolve "none yet"), so
      // wait it out. setCalcStatus above stops further saves being scheduled, and any that slipped
      // through reads persistedProjectIdRef rather than creating a second project.
      await drainDraftSaveQueue(draftSaveQueueRef.current);

      const persistResult = await persistFieldImport({
        klimFarmId: selectedFarmId,
        fieldId: selectedFieldId,
        cropYears,
        // Ref first (scenarioRefs lags); soilSightProjectId is the active farm's, and switchFarm lags too.
        getTargetProjectId: () => persistedProjectIdRef.current ?? scenarioRefs.projectId ?? activeFarmProjectId,
      });

      persistedProjectIdRef.current = persistResult.soilSightProjectId;
      // Refresh user.farm.soilSightProjectId so the gates recognise the new project.
      await reloadUserData().catch(() => {});

      // The persist already fetched the scenario list — reuse it instead of a second GET.
      const scenarios = persistResult.scenarios ?? (await listSoilSightScenarios(persistResult.soilSightProjectId));
      const { referenceScenarioId } = persistResult;

      // Created without the opt-in practices — step 3's cover-crop toggle adds them on request.
      const { initialChangeYears } = generateChangeScenario(persistResult.cropYears, buildCropCalendarInfoById(practiceCrops), new Date().getFullYear());

      let changeScenarioId = scenarios.find((scenario) => !scenario.isReference)?.id ?? null;
      if (!changeScenarioId) {
        const createdScenario = await createScenario.mutateAsync({
          soilSightProjectId: persistResult.soilSightProjectId,
          // t() resolves in the user's active website language, so the scenario is stored
          // in the BE with the language the user was onboarded in.
          body: { name: t("onboarding.changeScenarioName") },
        });
        changeScenarioId = createdScenario?.scenarioId ?? null;
      }
      if (!changeScenarioId) {
        throw new Error("Could not resolve change scenario");
      }

      await replaceCropYears.mutateAsync({
        scenarioId: changeScenarioId,
        body: { cropYears: initialChangeYears.map((year, index) => toApiCropYear(year, index + 1)) },
      });

      // Everything is persisted — commit the flow state before running the calculation, so a
      // calculation failure can degrade gracefully instead of losing the generated scenario.
      setScenarioRefs({ projectId: persistResult.soilSightProjectId, baselineScenarioId: referenceScenarioId, changeScenarioId });
      setCropYears(persistResult.cropYears);
      setChangeCropYears(initialChangeYears);

      // A field created in step 1 may not have its region yet, and /run cannot calculate without one
      try {
        await resolveFieldGeometry(selectedFieldId);
      } catch {
        // best effort — the calculation below reports the real outcome to the user
      }

      try {
        const runStartedAt = performance.now();
        const outcomes = await mutateOutcomes.mutateAsync(persistResult.soilSightProjectId);
        setOutcomes(outcomes);
        setCalcStatus("idle");
        productAnalytics.track("soilsights_onboarding:forecast_generate", {
          lift_classification: snakeCase(classifyLift(getScenarioUpliftPercent(outcomes, referenceScenarioId, changeScenarioId))),
          years_count: persistResult.cropYears.length,
          duration_ms: Math.round(performance.now() - runStartedAt),
        });
      } catch (runError) {
        // Calculation failed or timed out — step 2 shows "—" and the user
        // may still proceed and build the change scenario manually (edits retry the calc).
        setCalcStatus("error");
        productAnalytics.track("soilsights_onboarding:forecast_fail", { step_index: 2, error_type: classifyForecastError(runError) });
      }

      await queryClient.invalidateQueries({ queryKey: [LIST_SOIL_SIGHT_SCENARIOS, persistResult.soilSightProjectId] });
      return true;
    } catch (submitError) {
      setCalcStatus("error");
      productAnalytics.track("soilsights_onboarding:forecast_fail", { step_index: 2, error_type: "save_failed" });
      Message({
        content: getUserFacingApiErrorMessage(submitError, t("onboarding.submitError")),
        icon: "error",
      });
      return false;
    }
  }, [
    cropYears,
    selectedFarmId,
    selectedFieldId,
    importMeta?.importedFieldId,
    importMeta?.bannerDismissed,
    onboardingState,
    scenarioRefs.projectId,
    practiceCrops,
    persistFieldImport,
    user?.farm?.id,
    user?.farm?.soilSightProjectId,
    t,
    navigate,
    queryClient,
    reloadUserData,
    setValidationAttempted,
    setCalcStatus,
    setScenarioRefs,
    setCropYears,
    setChangeCropYears,
    setOutcomes,
    updateOnboardingSetting,
    createScenario,
    replaceCropYears,
    mutateOutcomes,
    draftSaveQueueRef,
    persistedProjectIdRef,
  ]);

  return { submitBaseline, isSubmitting: calcStatus === "calculating" };
};
